import React, { useEffect, useState } from 'react'

import type { AxiosError, AxiosResponse } from 'axios'
import { Field, FieldArray, Form, Formik } from 'formik'
import { Col, Row } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AsyncSelect from 'react-select/async'

import { invoiceTotals, lineAmounts, normalizeDiscount, round2 } from './invoiceMath'
import { useAuth } from '../common/Auth'
import FormErrorMessage from '../common/FormErrorMessage'
import { fetchClients, generateAndDownloadInvoicePdfFile, loadOptionsForCompanySelector } from '../common/helpers'
import { datePickerDateFormat } from '../common/i18n'
import NiceButton from '../common/NiceButton'
import { RequiredMark } from '../common/RequiredMark'
import { SendInvoiceDialog } from '../common/SendInvoiceDialog'
import { ClientFormWrapper } from '../common/styledComponents'
import axiosClient, { SERVER_URL } from '../service/axiosClient'
import ClientDTO from '../types/Client'
import { CorporateBody, CorporateOption } from '../types/Corporate'
import { countryOptions, HOME_COUNTRY } from '../types/Country'
import { currencies, formatMoney, HOME_CURRENCY } from '../types/Currency'
import { InvoiceFormData, InvoiceItemDto, PaymentType } from '../types/Invoice'


const getFormaUhradyOptions = (t: (k: string) => string) => [
    {value: PaymentType.BANK_TRANSFER, label: t('bank_transfer')},
    {value: PaymentType.CASH, label: t('cash')},
]
const getInvoiceStatusOptions = (t: (k: string) => string) => [
    {value: 'PAID', label: t('paid')},
    {value: 'UNPAID', label: t('unpaid')},
]

// 0%, 5%, 19%, 23%
const dphOptions = [
    {value: '5', label: '5%'},
    {value: '19', label: '19%'},
    {value: '23', label: '23%'},
]

// One line per label, so the inputs of a row stay level however long a label is
const singleLine: React.CSSProperties = {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
}

const emptyItem = (): InvoiceItemDto => ({
    dph: 0,
    dphValue: 0,
    price: 0,
    unitPrice: 0,
    title: '',
    quantity: 1,
    totalWithDph: 0,
    totalWithoutDph: 0,
    description: '',
    unit: 'ks',
    discount: 0,
    sku: ''
})

// The server takes plain yyyy-mm-dd, read as the local day - toISOString would shift it to UTC
const toLocalIsoDate = (value: Date): string => {
    const month = String(value.getMonth() + 1).padStart(2, '0')
    const day = String(value.getDate()).padStart(2, '0')
    return `${value.getFullYear()}-${month}-${day}`
}

const parseIsoDate = (value: string): Date | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
    return match ? new Date(+match[1], +match[2] - 1, +match[3]) : null
}

interface Props {
    id?: number
}

let generatePdf = false
let sendPdf = false
export const InvoiceForm = ({id}: Props) => {
    const {user, updateUser} = useAuth()
    const [searchParams,] = useSearchParams()
    const [isLoading, setIsLoading] = useState<boolean>(false)

    // The invoice is saved before we ask where to send it, so the id waits here for the answer
    const [pendingSend, setPendingSend] = useState<{ id: string, email: string } | null>(null)
    const queryClientId = searchParams.get('clientId')
    console.log('id', id)
    console.log('id', typeof id)
    const navigate = useNavigate()
    const [clients, setClients] = useState<ClientDTO[]>([])
    const [defaultProviderName, setDefaultProviderName] = useState<string>(user?.businessName ?? '')
    const [defaultCustomerName, setDefaultCustomerName] = useState<string>('')
    const [invoice, setInvoice] = useState<InvoiceFormData>({
        constantId: '0308',
        specificSymbol: '',
        paidDate: '',
        currency: HOME_CURRENCY,
        exchangeRate: 1,
        discount: 0,
        providerCountry: HOME_COUNTRY,
        customerCountry: HOME_COUNTRY,
        deliveryName: '',
        deliveryStreet: '',
        deliveryCity: '',
        deliveryZipCode: '',
        deliveryCountry: '',
        createdAt: '',
        customerBankAccountNumber: '',
        customerBankCode: '',
        customerBankName: '',
        customerVatPayer: false,
        customerDph7aParagraph: false,
        customerBusinessName: '',
        customerCity: '',
        customerDic: '',
        customerEmail: '',
        customerFirstName: '',
        logo: user?.logo || '',
        signature: user?.signature || '',
        customerIban: '',
        customerIcDph: '',
        customerIco: '',
        customerLastName: '',
        customerPhone: '',
        customerStreet: '',
        customerSwift: '',
        customerWeb: '',
        preItemText: '',
        customerZipCode: '',
        invoiceCreatedByEmail: '',
        invoiceCreatedByName: '',
        invoiceCreationDate: new Date().toString(),
        invoiceDueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toString(),
        invoiceStatus: 'UNPAID',
        invoiceId: '',
        invoiceItems: [emptyItem()],
        orderDate: '',
        orderNumber: '',
        paymentType: PaymentType.BANK_TRANSFER,
        providerBankAccountNumber: user?.bankAccountNumber ?? '',
        providerBankCode: user?.bankCode ?? '',
        providerBankName: user?.bankName ?? '',
        providerBusinessName: user?.businessName ?? '',
        providerDph7aParagraph: !!user?.getdph7aParagraph,
        providerVatPayer: !!user?.vatPayer,
        providerCity: user?.billingCity ?? '',
        providerDic: user?.dic ?? '',
        providerEmail: user?.email ?? '',
        providerIban: user?.iban ?? '',
        providerIcDph: user?.icDph ?? '',
        providerIco: user?.ico ?? '',
        providerPhone: '',
        providerStreet: user?.billingStreet ?? '',
        providerSwift: user?.swift ?? '',
        providerWeb: '',
        providerZipCode: user?.billingZipCode ?? '',
        referenceId: '',
        serviceRealizationDate: new Date().toString(),
        total: 0,
        dphValue: 0,
        totalWithoutDph: 0,
        totalWithDph: 0,
        updatedAt: ''
    })
    const {t, i18n} = useTranslation()
    const countries = countryOptions(i18n.language)
    const formaUhradyOptions = getFormaUhradyOptions(t)
    const invoiceStatusOptions = getInvoiceStatusOptions(t)
    const [logoUrl, setLogoUrl] = useState<string>('')
    const [signatureUrl, setSignatureUrl] = useState<string>('')

    useEffect(() => {
        fetchClients().then(setClients).catch(console.error)
    }, [])

    function notExistingInvoice() {
        navigate('/app/invoices')
    }

    useEffect(() => {
        if (id && id > 0) {
            axiosClient.get('invoice/' + id)
                .then(response => {
                    console.log('response', response)
                    // const postTmp = response.data

                    if (!response.data.id) {
                        notExistingInvoice()
                        return
                    }

                    if (response.data.logo) {
                        setLogoUrl(`${SERVER_URL}/storage/${response.data.logo}`)
                    }
                    // else if (user?.logo) {
                    //     setLogoUrl(`${SERVER_URL}/storage/${user.logo}`)
                    // }

                    if (response.data.signature) {
                        setSignatureUrl(`${SERVER_URL}/storage/${response.data.signature}`)
                    }
                    // else if (user?.signature) {
                    //     setSignatureUrl(`${SERVER_URL}/storage/${user.signature}`)
                    // }


                    setInvoice({
                        preItemText: '',
                        ...response.data
                    })
                    if (response.data.providerBusinessName) {
                        setDefaultProviderName(response.data.providerBusinessName)
                    }
                    if (response.data.customerBusinessName) {
                        setDefaultCustomerName(response.data.customerBusinessName)
                    }
                }, (e) => {
                    notExistingInvoice()
                })
        } else {
            if (user?.logo) {
                setLogoUrl(`${SERVER_URL}/storage/${user.logo}`)
            }
            if (user?.signature) {
                setSignatureUrl(`${SERVER_URL}/storage/${user.signature}`)
            }
            setInvoice((prevState) => ({
                ...prevState,
                preItemText: t('invoice_text_default'),
                logo: user?.logo || '',
                signature: user?.signature || ''
            }))
            axiosClient.get('invoice/generate/next-invoice-parameters')
                .then(response => {
                        setInvoice(
                            (prevState) => ({
                                ...prevState,
                                invoiceId: response.data.invoiceId,
                                referenceId: response.data.referenceId
                            })
                        )
                    }
                )
                .catch((error) => {
                    toast.error(t('invoice_number_format_error'))
                })
        }
    }, [id])

    function sendPdfFile(id: string, email: string) {
        axiosClient.get('/invoice/generate/' + id).then(() => {
            axiosClient.get('/invoice/send-by-email/' + id, {params: {email}})
                .then(() => {
                    toast.success(t('invoice_sent_success'))
                    navigate('/app/invoices')
                })
                .catch(() => toast.error(t('invoice_send_error')))
        })
    }

    function generatePdfFile(id: string, invoiceId: string) {
        generateAndDownloadInvoicePdfFile(id, invoiceId)
    }

    const submit = async (values: InvoiceFormData) => {
        console.log('values', values)

        try {
            // const response = await updateProfile({...values})
            setIsLoading(true)
            // The server recomputes these the same way, so they only have to agree with it
            const totals = invoiceTotals(values.invoiceItems, values.discount)
            const request = {
                ...values,
                invoiceCreationDate: values.invoiceCreationDate ? new Date(values.invoiceCreationDate).toISOString().split('T')[0] : '',
                invoiceDueDate: values.invoiceDueDate ? new Date(values.invoiceDueDate).toISOString().split('T')[0] : '',
                serviceRealizationDate: values.serviceRealizationDate ? new Date(values.serviceRealizationDate).toISOString().split('T')[0] : '',
                totalWithDph: totals.totalWithDph,
                totalWithoutDph: totals.totalWithoutDph,
                dphValue: totals.dphValue,
                paidDate: values.invoiceStatus === 'PAID' && values.paidDate ? values.paidDate : null,
                exchangeRate: values.currency === HOME_CURRENCY ? 1 : values.exchangeRate,
                // Delivery always goes to the customer's address, so none is kept apart
                deliveryName: '',
                deliveryStreet: '',
                deliveryCity: '',
                deliveryZipCode: '',
                deliveryCountry: '',
                // logo: values.logo || user?.logo || '',
                // signature: values.signature || user?.signature || '',
                // invoiceDu: values.invoiceCreationDate ? new Date(values.invoiceCreationDate).toISOString().split('T')[0],

                // title: values.title ?? '',
                // description: values.description ?? '',
                // clientId: values.clientId as unknown as number,
                // socialNetwork: values.socialNetwork as SocialNetwork,
                // publishFrequency: values.publishFrequency as PublishFrequency,
                // photo: values.photo as string,
                // showInCalendar: values.showInCalendar,
                // hashTags: '',
                // publishAt: values.publishAt,
                // publishFrom: '',
                // publishUntil: '',
            }

            // if (id) {
            //     request.id = id
            // }
            //
            //
            // // if (values.publishAt) {
            // //     const publishAt = new Date(values.publishAt)
            // //     const hours = publishAt.getHours().toString().padStart(2, '0')
            // //     const minutes = publishAt.getMinutes().toString().padStart(2, '0')
            // //
            // //     request.publishAt = `${hours}:${minutes}`
            // // }
            // if (values.publishFrom) {
            //     const publishFrom = new Date(values.publishFrom)
            //     request.publishFrom = publishFrom.toISOString()
            // }
            // if (values.publishUntil) {
            //     const publishUntil = new Date(values.publishUntil)
            //     request.publishUntil = publishUntil.toISOString()
            // }

            const response = await axiosClient.post('invoice', request)
            await updateUser()

            if (id && id > 0) {
                if (generatePdf) {
                    generatePdfFile(id.toString(), values.invoiceId)
                } else if (sendPdf) {
                    setPendingSend({id: id.toString(), email: values.customerEmail ?? ''})
                } else {
                    window.scrollTo(0, 0)
                    navigate('/app/invoices')
                    toast.success(t('invoice_updated_success'))
                }

            } else if (response.data > 0) {
                if (generatePdf) {
                    generatePdfFile(response.data.toString(), values.invoiceId)
                } else if (sendPdf) {
                    setPendingSend({id: response.data.toString(), email: values.customerEmail ?? ''})
                } else {
                    navigate('/app/invoices')
                    toast.success(t('invoice_created_success'))
                }
            }
            // actions.resetForm()
        } catch (error: any) {
            console.log('error', error)
            toast.error(t('invoice_save_error'))
        } finally {
            setIsLoading(false)
        }
    }

    const validate = (values: Partial<InvoiceFormData>) => {
        const errors: any = {}

        values.invoiceItems?.forEach((item, index) => {
            const itemErrors: { title?: string; unitPrice?: string; quantity?: string; discount?: string } = {}

            if (!item.title) {
                itemErrors.title = t('required_field_error')
            }
            if (!item.unitPrice || item.unitPrice <= 0) {
                itemErrors.unitPrice = t('required_field_error')
            }
            if (!item.quantity || item.quantity <= 0) {
                itemErrors.quantity = t('required_field_error')
            }
            if (Number(item.discount) < 0 || Number(item.discount) > 100) {
                itemErrors.discount = t('discount_range_error')
            }

            if (Object.keys(itemErrors).length > 0) {
                if (!errors.invoiceItems) {
                    errors.invoiceItems = {}
                }
                errors.invoiceItems[index] = itemErrors
            }
        })

        if (!values?.invoiceId) {
            errors.invoiceId = t('required_field_error')
        }
        if (!values?.paymentType) {
            errors.paymentType = t('required_field_error')
        }
        // Without the rate the exports could not book the invoice in euros
        if (values?.currency && values.currency !== HOME_CURRENCY && !(Number(values.exchangeRate) > 0)) {
            errors.exchangeRate = t('exchange_rate_required')
        }
        if (Number(values?.discount) < 0 || Number(values?.discount) > 100) {
            errors.discount = t('discount_range_error')
        }

        if (!values?.invoiceCreationDate) {
            errors.invoiceCreationDate = t('required_field_error')
        }
        if (!values?.invoiceDueDate) {
            errors.invoiceDueDate = t('required_field_error')
        }
        if (!values?.serviceRealizationDate) {
            errors.serviceRealizationDate = t('required_field_error')
        }
        // A sole trader invoices under their own name, so the company name cannot be required.
        // The name can be, and is given for a company too.
        if (!values?.customerFirstName?.trim()) {
            errors.customerFirstName = t('required_field_error')
        }
        if (!values?.customerLastName?.trim()) {
            errors.customerLastName = t('required_field_error')
        }

        return errors
    }

    return (
        <>
            <Formik
                initialValues={invoice}
                enableReinitialize
                validate={validate}
                onSubmit={submit}
            >{({values, setFieldValue, submitForm}) => {
                // Every change to a line works its amounts out again, discount included
                const updateItem = (index: number, changes: Partial<InvoiceItemDto>) => {
                    const item = {...values.invoiceItems[index], ...changes}
                    setFieldValue(`invoiceItems[${index}]`, {...item, ...lineAmounts(item)})
                }
                // Not a VAT payer, a payer, or registered under §7/§7a - one of the three, the same
                // for both parties. The supplier's comes from the profile and is shown only.
                const vatStatus = (party: 'provider' | 'customer', disabled: boolean) => {
                    const payer = !!values[`${party}VatPayer`]
                    const paragraph7 = !!values[`${party}Dph7aParagraph`]
                    const set = (vatPayer: boolean, dph7a: boolean) => {
                        setFieldValue(`${party}VatPayer`, vatPayer)
                        setFieldValue(`${party}Dph7aParagraph`, dph7a)
                    }
                    const options = [
                        {key: 'none', label: t('not_vat_payer'), checked: !payer && !paragraph7, onChange: () => set(false, false)},
                        {key: 'payer', label: t('vat_payer'), checked: payer && !paragraph7, onChange: () => set(true, false)},
                        {key: 'paragraph7', label: t('vat_registered_7'), checked: paragraph7, onChange: () => set(false, true)},
                    ]
                    return (
                        <div className="form-group">
                            {options.map(option => (
                                <div className="form-check" key={option.key}>
                                    <input
                                        type="checkbox"
                                        id={`${party}-vat-${option.key}`}
                                        className="form-check-input"
                                        disabled={disabled}
                                        checked={option.checked}
                                        onChange={option.onChange}/>
                                    <label className="form-check-label" htmlFor={`${party}-vat-${option.key}`} style={singleLine}>
                                        {option.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                    )
                }
                const totals = invoiceTotals(values.invoiceItems, values.discount)
                const foreignCurrency = !!values.currency && values.currency !== HOME_CURRENCY

                return (<>
                    <Form>
                        {/*<PrettyPrintJson data={values}/>*/}

                        <div className="row d-flex mb-4">
                            <div className="col-md-12">
                                <h5 className="text-left mb-2">{t('general_info')}</h5>
                                <ClientFormWrapper>
                                    <div className="row mb-3">
                                        <div className="col-md-3">
                                            <div className="form-group">
                                                <label style={singleLine} htmlFor="invoiceId">{t('invoice_number')}<RequiredMark/></label>
                                                <Field id="invoiceId" name="invoiceId"
                                                       className="form-control"
                                                        onBlur={(e: Event) => {
                                                            const newInvoiceId = (e.target as HTMLInputElement).value
                                                            axiosClient.get(`/invoice/generate/reference-id/${newInvoiceId}`)
                                                                .then((response: AxiosResponse<string>) => setFieldValue('referenceId', response.data))
                                                                .catch((error: AxiosError) => {
                                                                    if (400 === error.response?.status) {
                                                                        toast.error(error.response?.data)
                                                                    }
                                                                })
                                                        }}/>
                                                <FormErrorMessage name="invoiceId"/>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-group">
                                                <label style={singleLine} htmlFor="orderNumber">{t('order_number')}</label>
                                                <Field id="orderNumber" name="orderNumber"
                                                       className="form-control"/>
                                                <FormErrorMessage name="orderNumber"/>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div
                                                className={'form-group'}>
                                                <label style={singleLine} htmlFor="paymentType">
                                                    {t('payment_method')}
                                                <RequiredMark/></label>
                                                <Field as="select" id="paymentType" name="paymentType"
                                                       className="form-select">
                                                    {formaUhradyOptions.map((option) => (
                                                        <option key={option.value}
                                                                value={option.value}>{option.label}</option>))}
                                                </Field>
                                                <FormErrorMessage name="paymentType"/>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-group">
                                                <label style={singleLine} htmlFor="referenceId">{t('variable_symbol')}</label>
                                                <Field id="referenceId" name="referenceId"
                                                       className="form-control"/>
                                                <FormErrorMessage name="referenceId"/>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-md-3">
                                            <div className="form-group">
                                                <label style={singleLine} htmlFor="invoiceCreationDate">{t('issue_date')}<RequiredMark/></label>
                                                <DatePicker
                                                    scrollableYearDropdown
                                                    yearDropdownItemNumber={100}
                                                    locale="sk"
                                                    dateFormat={datePickerDateFormat}
                                                    showYearDropdown
                                                    selected={(values.invoiceCreationDate && new Date(values.invoiceCreationDate)) || null}
                                                    onChange={(value: any) => {
                                                        setFieldValue('invoiceCreationDate', value)
                                                    }}
                                                    id="invoiceCreationDate"
                                                    name="invoiceCreationDate"
                                                    className="form-control"/>
                                                <FormErrorMessage name="invoiceCreationDate"/>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-group">
                                                <label style={singleLine} htmlFor="serviceRealizationDate">{t('delivery_date')}<RequiredMark/></label>
                                                <DatePicker
                                                    scrollableYearDropdown
                                                    yearDropdownItemNumber={100}
                                                    locale="sk"
                                                    dateFormat={datePickerDateFormat}
                                                    showYearDropdown
                                                    selected={(values.serviceRealizationDate && new Date(values.serviceRealizationDate)) || null}
                                                    onChange={(value: any) => {
                                                        setFieldValue('serviceRealizationDate', value)
                                                    }}
                                                    id="serviceRealizationDate"
                                                    name="serviceRealizationDate"
                                                    className="form-control"/>
                                                <FormErrorMessage name="serviceRealizationDate"/>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-group">
                                                <label style={singleLine} htmlFor="invoiceDueDate">{t('due_date')}<RequiredMark/></label>
                                                <DatePicker
                                                    scrollableYearDropdown
                                                    yearDropdownItemNumber={100}
                                                    locale="sk"
                                                    dateFormat={datePickerDateFormat}
                                                    showYearDropdown
                                                    selected={(values.invoiceDueDate && new Date(values.invoiceDueDate)) || null}
                                                    onChange={(value: any) => {
                                                        setFieldValue('invoiceDueDate', value)
                                                    }}
                                                    id="invoiceDueDate"
                                                    name="invoiceDueDate"
                                                    className="form-control"/>
                                                <FormErrorMessage name="invoiceDueDate"/>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div
                                                className={'form-group'}>
                                                <label style={singleLine} htmlFor="invoiceStatus">
                                                    {t('invoice_status')}
                                                </label>
                                                <Field as="select" id="invoiceStatus" name="invoiceStatus"
                                                       className="form-select"
                                                       onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                                           const status = e.target.value
                                                           setFieldValue('invoiceStatus', status)
                                                           // Marking it paid dates the payment today unless a day is picked
                                                           if (status === 'PAID' && !values.paidDate) {
                                                               setFieldValue('paidDate', toLocalIsoDate(new Date()))
                                                           } else if (status !== 'PAID') {
                                                               setFieldValue('paidDate', '')
                                                           }
                                                       }}>
                                                    {invoiceStatusOptions.map((option) => (
                                                        <option key={option.value}
                                                                value={option.value}>{option.label}</option>))}
                                                </Field>
                                                <FormErrorMessage name="invoiceStatus"/>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-md-3">
                                            <div className="form-group">
                                                <label style={singleLine} htmlFor="constantId">{t('constant_symbol')}</label>
                                                <Field id="constantId" name="constantId" className="form-control"/>
                                                <FormErrorMessage name="constantId"/>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-group">
                                                <label style={singleLine} htmlFor="specificSymbol">{t('specific_symbol')}</label>
                                                <Field id="specificSymbol" name="specificSymbol" className="form-control"/>
                                                <FormErrorMessage name="specificSymbol"/>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-group">
                                                <label style={singleLine} htmlFor="currency">{t('currency')}</label>
                                                <Field as="select" id="currency" name="currency" className="form-select"
                                                       onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                                           setFieldValue('currency', e.target.value)
                                                           if (e.target.value === HOME_CURRENCY) {
                                                               setFieldValue('exchangeRate', 1)
                                                           }
                                                       }}>
                                                    {currencies.map((currency) => (
                                                        <option key={currency} value={currency}>{currency}</option>))}
                                                </Field>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-group">
                                                <label style={singleLine} htmlFor="exchangeRate">
                                                    {t('exchange_rate', {currency: values.currency || HOME_CURRENCY})}
                                                    {foreignCurrency && <RequiredMark/>}
                                                </label>
                                                <Field id="exchangeRate" name="exchangeRate" type="number" step="0.000001"
                                                       min="0" disabled={!foreignCurrency} className="form-control"/>
                                                <FormErrorMessage name="exchangeRate"/>
                                            </div>
                                        </div>
                                    </div>
                                    {values.invoiceStatus === 'PAID' && (
                                        <div className="row mb-3">
                                            <div className="col-md-3">
                                                <div className="form-group">
                                                    <label style={singleLine} htmlFor="paidDate">{t('paid_date')}</label>
                                                    <DatePicker
                                                        scrollableYearDropdown
                                                        yearDropdownItemNumber={100}
                                                        locale="sk"
                                                        dateFormat={datePickerDateFormat}
                                                        showYearDropdown
                                                        selected={values.paidDate ? parseIsoDate(values.paidDate) : null}
                                                        onChange={(value: Date | null) => {
                                                            setFieldValue('paidDate', value ? toLocalIsoDate(value) : '')
                                                        }}
                                                        id="paidDate"
                                                        name="paidDate"
                                                        className="form-control"/>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </ClientFormWrapper>
                            </div>
                        </div>


                        {/* Supplier and customer carry the same fields in the same rows, so the two
                            columns line up. The delivery always goes to the customer's address. */}
                        <div className="row d-flex mb-4">
                            <div className="col-md-6 d-flex flex-column">
                                <h5 className="text-left mb-2">{t('supplier')}</h5>
                                <ClientFormWrapper className="flex-grow-1">
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <div className="form-group">
                                                <label htmlFor="providerBusinessName" style={singleLine}>{t('company_name')}</label>
                                                <AsyncSelect
                                                    placeholder={t('search_company')}
                                                    isDisabled={true}
                                                    className={'async-select'}
                                                    {...(defaultProviderName ? {
                                                        value: {
                                                            label: defaultProviderName,
                                                            value: {} as CorporateBody
                                                        } as CorporateOption
                                                    } : null)}
                                                    classNamePrefix={'async-select'}
                                                    loadOptions={loadOptionsForCompanySelector}/>
                                                <Field id="providerBusinessName" name="providerBusinessName"
                                                       className="form-control d-none"/>
                                                <FormErrorMessage name="providerBusinessName"/>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="invoiceCreatedByName" style={singleLine}>{t('invoice_created_by')}</label>
                                                <Field id="invoiceCreatedByName" name="invoiceCreatedByName"
                                                       className="form-control"/>
                                                <FormErrorMessage name="invoiceCreatedByName"/>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="invoiceCreatedByEmail" style={singleLine}>{t('email')}</label>
                                                <Field id="invoiceCreatedByEmail" name="invoiceCreatedByEmail"
                                                       className="form-control"/>
                                                <FormErrorMessage name="invoiceCreatedByEmail"/>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <div className="form-group">
                                                <label htmlFor="providerIco" style={singleLine}>{t('ico')}</label>
                                                <Field id="providerIco" name="providerIco" disabled
                                                       className="form-control"/>
                                                <FormErrorMessage name="providerIco"/>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <div className="form-group">
                                                <label htmlFor="providerDic" style={singleLine}>{t('dic')}</label>
                                                <Field id="providerDic" name="providerDic" disabled
                                                       className="form-control"/>
                                                <FormErrorMessage name="providerDic"/>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <div className="form-group">
                                                <label htmlFor="providerIcDph" style={singleLine}>{t('ic_dph')}</label>
                                                <Field id="providerIcDph" name="providerIcDph" disabled
                                                       className="form-control"/>
                                                <FormErrorMessage name="providerIcDph"/>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            {vatStatus('provider', true)}
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <div className="form-group">
                                                <label htmlFor="providerStreet" style={singleLine}>{t('street')}</label>
                                                <Field id="providerStreet" name="providerStreet" disabled
                                                       className="form-control"/>
                                                <FormErrorMessage name="providerStreet"/>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-md-8">
                                            <div className="form-group">
                                                <label htmlFor="providerCity" style={singleLine}>{t('city')}</label>
                                                <Field id="providerCity" name="providerCity" disabled
                                                       className="form-control"/>
                                                <FormErrorMessage name="providerCity"/>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="form-group">
                                                <label htmlFor="providerZipCode" style={singleLine}>{t('zip')}</label>
                                                <Field id="providerZipCode" name="providerZipCode" disabled
                                                       className="form-control"/>
                                                <FormErrorMessage name="providerZipCode"/>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <div className="form-group">
                                                <label htmlFor="providerCountry" style={singleLine}>{t('country')}</label>
                                                <Field as="select" id="providerCountry" name="providerCountry" className="form-select">
                                                    {countries.map((country) => (
                                                        <option key={country.value} value={country.value}>{country.label}</option>))}
                                                </Field>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <div className="form-group">
                                                <label htmlFor="providerIban" style={singleLine}>{t('iban')}</label>
                                                <Field id="providerIban" name="providerIban" disabled
                                                       className="form-control"/>
                                                <FormErrorMessage name="providerIban"/>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="providerEmail" style={singleLine}>{t('email')}</label>
                                                <Field id="providerEmail" name="providerEmail" disabled
                                                       className="form-control"/>
                                                <FormErrorMessage name="providerEmail"/>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="providerPhone" style={singleLine}>{t('phone')}</label>
                                                <Field id="providerPhone" name="providerPhone" disabled
                                                       className="form-control"/>
                                                <FormErrorMessage name="providerPhone"/>
                                            </div>
                                        </div>
                                    </div>
                                </ClientFormWrapper>
                            </div>
                            <div className="col-md-6 d-flex flex-column">
                                <h5 className="text-left mb-2">{t('recipient')}</h5>
                                <ClientFormWrapper className="flex-grow-1">
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <div className="form-group">
                                                <label htmlFor="customerBusinessName" style={singleLine}>{t('company_name')}</label>
                                                <AsyncSelect
                                                    className={'async-select'}
                                                    classNamePrefix={'async-select'}
                                                    {...(defaultCustomerName ? {
                                                        value: {
                                                            label: defaultCustomerName,
                                                            value: {} as CorporateBody
                                                        } as CorporateOption
                                                    } : null)}
                                                    placeholder={t('search_company')}
                                                    loadingMessage={() => <>{t('searching')}</>}
                                                    noOptionsMessage={(inputValue) => <>{inputValue.inputValue.length ? t('no_search_results') : t('start_typing')}</>}
                                                    onChange={(item) => {
                                                        setDefaultCustomerName(item?.label as string)
                                                        setFieldValue('customerBusinessName', item?.label)
                                                        setFieldValue('customerIco', item?.value?.cin)
                                                        setFieldValue('customerDic', item?.value?.tin)
                                                        setFieldValue('customerIcDph', item?.value?.vatin)
                                                        setFieldValue('customerStreet', item?.value?.formatted_street)
                                                        setFieldValue('customerCity', item?.value?.municipality)
                                                        setFieldValue('customerZipCode', item?.value?.postal_code)
                                                        // The register says under which paragraph the company is registered for VAT
                                                        const paragraph = item?.value?.vatin_paragraph
                                                        setFieldValue('customerVatPayer', paragraph === '§4')
                                                        setFieldValue('customerDph7aParagraph', paragraph === '§7' || paragraph === '§7a')
                                                    }}
                                                    cacheOptions
                                                    loadOptions={loadOptionsForCompanySelector}/>
                                                <Field id="customerBusinessName" name="customerBusinessName"
                                                       className="form-control d-none"/>
                                                <FormErrorMessage name="customerBusinessName"/>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="customerFirstName" style={singleLine}>{t('first_name')}<RequiredMark/></label>
                                                <Field id="customerFirstName" name="customerFirstName"
                                                       className="form-control"/>
                                                <FormErrorMessage name="customerFirstName"/>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="customerLastName" style={singleLine}>{t('last_name')}<RequiredMark/></label>
                                                <Field id="customerLastName" name="customerLastName"
                                                       className="form-control"/>
                                                <FormErrorMessage name="customerLastName"/>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <div className="form-group">
                                                <label htmlFor="customerIco" style={singleLine}>{t('ico')}</label>
                                                <Field id="customerIco" name="customerIco"
                                                       className="form-control"/>
                                                <FormErrorMessage name="customerIco"/>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <div className="form-group">
                                                <label htmlFor="customerDic" style={singleLine}>{t('dic')}</label>
                                                <Field id="customerDic" name="customerDic"
                                                       className="form-control"/>
                                                <FormErrorMessage name="customerDic"/>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <div className="form-group">
                                                <label htmlFor="customerIcDph" style={singleLine}>{t('ic_dph')}</label>
                                                <Field id="customerIcDph" name="customerIcDph"
                                                       className="form-control"/>
                                                <FormErrorMessage name="customerIcDph"/>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            {vatStatus('customer', false)}
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <div className="form-group">
                                                <label htmlFor="customerStreet" style={singleLine}>{t('street')}</label>
                                                <Field id="customerStreet" name="customerStreet"
                                                       className="form-control"/>
                                                <FormErrorMessage name="customerStreet"/>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-md-8">
                                            <div className="form-group">
                                                <label htmlFor="customerCity" style={singleLine}>{t('city')}</label>
                                                <Field id="customerCity" name="customerCity"
                                                       className="form-control"/>
                                                <FormErrorMessage name="customerCity"/>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="form-group">
                                                <label htmlFor="customerZipCode" style={singleLine}>{t('zip')}</label>
                                                <Field id="customerZipCode" name="customerZipCode"
                                                       className="form-control"/>
                                                <FormErrorMessage name="customerZipCode"/>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <div className="form-group">
                                                <label htmlFor="customerCountry" style={singleLine}>{t('country')}</label>
                                                <Field as="select" id="customerCountry" name="customerCountry" className="form-select">
                                                    {countries.map((country) => (
                                                        <option key={country.value} value={country.value}>{country.label}</option>))}
                                                </Field>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <div className="form-group">
                                                <label htmlFor="customerIban" style={singleLine}>{t('iban')}</label>
                                                <Field id="customerIban" name="customerIban"
                                                       className="form-control"/>
                                                <FormErrorMessage name="customerIban"/>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="customerEmail" style={singleLine}>{t('email')}</label>
                                                <Field id="customerEmail" name="customerEmail"
                                                       className="form-control"/>
                                                <FormErrorMessage name="customerEmail"/>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="customerPhone" style={singleLine}>{t('phone')}</label>
                                                <Field id="customerPhone" name="customerPhone"
                                                       className="form-control"/>
                                                <FormErrorMessage name="customerPhone"/>
                                            </div>
                                        </div>
                                    </div>
                                </ClientFormWrapper>
                            </div>
                        </div>
                        <div className="row d-flex mb-4">
                            <div className="col-md-12">
                                <h5 className="text-left mb-1">{t('text_before_items')}</h5>
                                <ClientFormWrapper>
                                    <div className="form-group">
                                        <Field
                                            id="preItemText"
                                            name="preItemText"
                                            className="form-control"/>
                                        <FormErrorMessage name="preItemText"/>
                                    </div>
                                </ClientFormWrapper>
                            </div>
                        </div>

                        <div className="row d-flex mb-4">
                            <div className="col-md-12">
                                <h5 className="text-left mb-1">{t('items')}</h5>
                                <FieldArray
                                    name="invoiceItems"
                                    render={arrayHelpers => (
                                        <>
                                            {Array.isArray(values.invoiceItems) ? values.invoiceItems.map((service, index) => (
                                                <ClientFormWrapper key={index} className="mb-2 position-relative">
                                                    {index !== 0 &&
                                                        <i className="bi bi-x-lg position-absolute cursor-pointer"
                                                           onClick={() => {
                                                               arrayHelpers.remove(index)
                                                           }}
                                                           style={{top: '5px', right: '10px'}}></i>}
                                                    <div className="row mb-3">
                                                        <div className="col-md-4">
                                                            <div className="form-group">
                                                                <label style={singleLine} htmlFor={`invoiceItems[${index}].title`}>{t('item_name')}<RequiredMark/></label>
                                                                <Field
                                                                    className="form-control"
                                                                    id={`invoiceItems[${index}].title`}
                                                                    name={`invoiceItems[${index}].title`}/>
                                                                <FormErrorMessage
                                                                    name={`invoiceItems[${index}].title`}/>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-2">
                                                            <div className="form-group mb-0">
                                                                <label style={singleLine}
                                                                    htmlFor={`invoiceItems[${index}].quantity`}>{t('quantity')}<RequiredMark/></label>
                                                                <input
                                                                    className="form-control"
                                                                    type="number"
                                                                    value={service.quantity}
                                                                    id={`invoiceItems[${index}].quantity`}
                                                                    name={`invoiceItems[${index}].quantity`}
                                                                    onChange={(e) => updateItem(index, {quantity: e.target.value as unknown as number})}/>
                                                                <FormErrorMessage
                                                                    name={`invoiceItems[${index}].quantity`}/>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-2">
                                                            <div className="form-group mb-0">
                                                                <label style={singleLine} htmlFor={`invoiceItems[${index}].unitPrice`}>{t('unit_price')}<RequiredMark/></label>
                                                                <input
                                                                    className="form-control"
                                                                    type="number"
                                                                    value={service.unitPrice}
                                                                    id={`invoiceItems[${index}].unitPrice`}
                                                                    name={`invoiceItems[${index}].unitPrice`}
                                                                    onChange={(e) => updateItem(index, {unitPrice: e.target.value as unknown as number})}/>
                                                                <FormErrorMessage
                                                                    name={`invoiceItems[${index}].unitPrice`}/>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-2">
                                                            <div className="form-group mb-0">
                                                                <label style={singleLine} htmlFor={`invoiceItems[${index}].dph`}>{t('vat_rate')}</label>
                                                                <select
                                                                    id={`invoiceItems[${index}].dph`}
                                                                    name={`invoiceItems[${index}].dph`}
                                                                    className="form-select"
                                                                    value={service.dph + ''}
                                                                    onChange={(e) => updateItem(index, {dph: parseInt(e.target.value)})}>
                                                                    <option value="0">0%</option>
                                                                    {values.providerVatPayer && dphOptions.map((option) => (
                                                                        <option
                                                                            key={option.value}
                                                                            value={option.value}>
                                                                            {option.label}
                                                                        </option>))}
                                                                </select>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-2">
                                                            <div className="form-group mb-0">
                                                                <label style={singleLine} htmlFor={`invoiceItems[${index}].totalWithDph`}>{t('total_price')}</label>
                                                                <input
                                                                    className="form-control"
                                                                    readOnly
                                                                    id={`invoiceItems[${index}].totalWithDph`}
                                                                    value={formatMoney(lineAmounts(service).totalWithDph, values.currency)}/>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="row mb-3">
                                                        <div className="col-md-6">
                                                            <div className="form-group mb-0">
                                                                <label style={singleLine} htmlFor={`invoiceItems[${index}].description`}>{t('description')}</label>
                                                                <Field
                                                                    className="form-control"
                                                                    id={`invoiceItems[${index}].description`}
                                                                    name={`invoiceItems[${index}].description`}/>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-2">
                                                            <div className="form-group mb-0">
                                                                <label style={singleLine} htmlFor={`invoiceItems[${index}].unit`}>{t('unit')}</label>
                                                                <Field
                                                                    className="form-control"
                                                                    id={`invoiceItems[${index}].unit`}
                                                                    name={`invoiceItems[${index}].unit`}/>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-2">
                                                            <div className="form-group mb-0">
                                                                <label style={singleLine} htmlFor={`invoiceItems[${index}].discount`}>{t('discount_percent')}</label>
                                                                <input
                                                                    className="form-control"
                                                                    type="number"
                                                                    min="0"
                                                                    max="100"
                                                                    value={service.discount ?? 0}
                                                                    id={`invoiceItems[${index}].discount`}
                                                                    name={`invoiceItems[${index}].discount`}
                                                                    onChange={(e) => updateItem(index, {discount: e.target.value as unknown as number})}/>
                                                                <FormErrorMessage
                                                                    name={`invoiceItems[${index}].discount`}/>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-2">
                                                            <div className="form-group mb-0">
                                                                <label style={singleLine} htmlFor={`invoiceItems[${index}].sku`}>{t('sku')}</label>
                                                                <Field
                                                                    className="form-control"
                                                                    id={`invoiceItems[${index}].sku`}
                                                                    name={`invoiceItems[${index}].sku`}/>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </ClientFormWrapper>
                                            )) : null}

                                            <button
                                                onClick={() => {
                                                    arrayHelpers.push(emptyItem())
                                                    // setFieldValue('invoiceItems', [...values.invoiceItems, {}])
                                                }}
                                                type="button"
                                                className="btn btn-primary mt-2 btn-amani w-auto">
                                                {t('add_item')}
                                            </button>
                                        </>
                                    )}
                                />

                                {/*{values.invoiceItems.map((service, index) => (*/}
                                {/*    <ClientFormWrapper key={index} className="mb-2 position-relative">*/}
                                {/*        {index !== 0 && <i className="bi bi-x-lg position-absolute cursor-pointer"*/}
                                {/*            onClick={() => {*/}
                                {/*                console.log('index', index)*/}
                                {/*                const currentItems = [...values.invoiceItems]*/}
                                {/*                console.log('currentItems', currentItems)*/}
                                {/*                currentItems.splice(index, 1)*/}
                                {/*                setFieldValue('invoiceItems', currentItems)*/}
                                {/*            }}*/}
                                {/*            style={{top: '5px', right: '10px'}}></i>}*/}
                                {/*        <div className="row mb-3">*/}
                                {/*            <div className="col-md-4">*/}
                                {/*                <div className="form-group">*/}
                                {/*                    <label style={singleLine} htmlFor="total">Názov</label>*/}
                                {/*                    <Field id="total" name="total"*/}
                                {/*                           className="form-control"/>*/}
                                {/*                    <FormErrorMessage name="total"/>*/}
                                {/*                </div>*/}
                                {/*            </div>*/}
                                {/*            <div className="col-md-4">*/}
                                {/*                <div className="form-group mb-0">*/}
                                {/*                    <label style={singleLine} htmlFor="inputEmail3">Dátum dodania</label>*/}

                                {/*                    <input type="email" className="form-control" id="inputEmail3"/>*/}
                                {/*                </div>*/}
                                {/*            </div>*/}
                                {/*            <div className="col-md-4">*/}
                                {/*                <div className="form-group mb-0">*/}
                                {/*                    <label style={singleLine} htmlFor="inputEmail3">Dátum splatnosti</label>*/}

                                {/*                    <input type="email" className="form-control" id="inputEmail3"/>*/}
                                {/*                </div>*/}
                                {/*            </div>*/}

                                {/*        </div>*/}
                                {/*    </ClientFormWrapper>)*/}
                                {/*)}*/}

                                {/*<button*/}
                                {/*    onClick={() => {*/}
                                {/*        setFieldValue('invoiceItems', [...values.invoiceItems, {}])*/}
                                {/*    }}*/}
                                {/*    type="button"*/}
                                {/*    className="btn btn-primary mt-2 btn-amani w-auto">*/}
                                {/*    Pridať položku*/}
                                {/*</button>*/}
                            </div>
                        </div>

                        <div className="row d-flex mb-4">
                            <div className="col-md-12">
                                <h5 className="text-left mb-2">{t('total_sums')}</h5>
                                <ClientFormWrapper>
                                    <div className="row mb-3">
                                        <div className="col-md-4">
                                            <div className="form-group">
                                                <label style={singleLine} htmlFor="discount">{t('document_discount')}</label>
                                                <Field id="discount" name="discount" type="number" min="0" max="100"
                                                       className="form-control"/>
                                                <FormErrorMessage name="discount"/>
                                            </div>
                                        </div>
                                        {normalizeDiscount(values.discount) > 0 && (
                                            <div className="col-md-8">
                                                <div className="form-group">
                                                    <label>{t('items_subtotal')}</label>
                                                    <input className="form-control" readOnly
                                                           value={`${formatMoney(totals.itemsWithDph, values.currency)} − ${formatMoney(round2(totals.itemsWithDph - totals.totalWithDph), values.currency)}`}/>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="row">
                                        <div className="col-md-4">
                                            <div className="form-group">
                                                <label style={singleLine} htmlFor="totalWithoutDph">{t('total')}</label>
                                                <input
                                                    id="totalWithoutDph"
                                                    className="form-control"
                                                    readOnly
                                                    value={formatMoney(totals.totalWithoutDph, values.currency)}/>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="form-group">
                                                <label style={singleLine} htmlFor="dphValue">{t('vat')}</label>
                                                <input
                                                    id="dphValue"
                                                    readOnly
                                                    value={formatMoney(totals.dphValue, values.currency)}
                                                    className="form-control"/>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="form-group">
                                                <label style={singleLine} htmlFor="totalWithDph">{t('total_with_vat')}</label>
                                                <input
                                                    id="totalWithDph"
                                                    readOnly
                                                    className={'form-control'}
                                                    value={formatMoney(totals.totalWithDph, values.currency)}/>
                                            </div>
                                        </div>
                                    </div>
                                </ClientFormWrapper>
                            </div>
                        </div>

                        <div className="row d-flex mb-4">
                            <div className="col-md-12">
                                <h5 className="text-left mb-2">{t('note')}</h5>
                                <ClientFormWrapper>
                                    <div className="form-group">
                                        <Field
                                            as="textarea"
                                            id="note"
                                            name="note"
                                            className="form-control"/>
                                        <FormErrorMessage name="dphValue"/>
                                    </div>
                                </ClientFormWrapper>
                            </div>
                        </div>

                        <div className="row d-flex mb-4">
                            <div className="col-md-12">
                                <h5 className="text-left mb-2">{t('additional_info')}</h5>
                                <ClientFormWrapper>
                                    <Row>
                                        <Col>
                                            <div className="form-group">
                                                <label style={singleLine}
                                                    htmlFor="logo">{t('logo')}</label>
                                                {logoUrl ?
                                                    <>
                                                        <img src={logoUrl as unknown as string}
                                                             style={{width: '100px', display: 'block'}}/>
                                                        <button
                                                            onClick={() => {
                                                                setLogoUrl('')
                                                                setFieldValue('logo', '')
                                                            }}
                                                            type="button"
                                                            className="button btn btn-primary btn-amani mt-3 mb-2">{t('delete_logo')}</button>
                                                    </> : null}

                                                <label className="btn btn-like-input d-block mt-1"
                                                       htmlFor="logo">{t('upload_logo')}</label>
                                                <input className={'d-none'} id="logo" name="logo" type="file"
                                                       onChange={(event) => {
                                                           if (event.target.files && event.target.files[0]) {
                                                               const formData = new FormData()
                                                               formData.append('file', event.target.files[0])

                                                               axiosClient.post('storage', formData).then((resp) => {
                                                                   console.log('resp', resp.data)
                                                                   setFieldValue('logo', resp.data)
                                                                   setLogoUrl(`${SERVER_URL}/storage/${resp.data}`)
                                                               })
                                                           }
                                                       }}/>
                                            </div>
                                        </Col>
                                        <Col>
                                            <div className="form-group">
                                                <label style={singleLine}
                                                    htmlFor="signature">{t('signature')}</label>
                                                {signatureUrl ?
                                                    <>
                                                        <img src={signatureUrl as unknown as string}
                                                             style={{width: '100px', display: 'block'}}/>
                                                        <button
                                                            onClick={() => {
                                                                setSignatureUrl('')
                                                                setFieldValue('signature', '')
                                                            }}
                                                            type="button"
                                                            className="button btn btn-primary btn-amani mt-3 mb-2">{t('delete_signature')}</button>
                                                    </> : null}

                                                <label className="btn btn-like-input d-block mt-1"
                                                       htmlFor="signature">{t('upload_signature')}</label>
                                                <input className={'d-none'} id="signature" name="signature" type="file"
                                                       onChange={(event) => {
                                                           if (event.target.files && event.target.files[0]) {
                                                               const formData = new FormData()
                                                               formData.append('file', event.target.files[0])

                                                               axiosClient.post('storage', formData).then((resp) => {
                                                                   console.log('resp', resp.data)
                                                                   setFieldValue('signature', resp.data)
                                                                   setSignatureUrl(`${SERVER_URL}/storage/${resp.data}`)
                                                               })
                                                           }
                                                       }}/>
                                            </div>
                                        </Col>
                                    </Row>
                                </ClientFormWrapper>
                            </div>
                        </div>

                        <div className="d-flex gap-2 mb-4">
                            <NiceButton type="button" onClick={() => {
                                generatePdf = false
                                sendPdf = false
                                submitForm()
                            }} isLoading={isLoading}>
                                {t('save')}
                            </NiceButton>
                            <NiceButton
                                type="button"
                                onClick={() => {
                                    generatePdf = true
                                    sendPdf = false
                                    submitForm()
                                }}
                                isLoading={isLoading}>
                                {t('save_and_download')}
                            </NiceButton>
                            <NiceButton
                                type="button"
                                onClick={() => {
                                    generatePdf = false
                                    sendPdf = true
                                    submitForm()
                                }}
                                isLoading={isLoading}>
                                {t('save_and_send')}
                            </NiceButton>
                        </div>

                    </Form>
                </>)
            }}</Formik>
            <SendInvoiceDialog show={!!pendingSend}
                               defaultEmail={pendingSend?.email}
                               onClose={() => setPendingSend(null)}
                               onConfirm={(email: string) => {
                                   if (pendingSend) {
                                       sendPdfFile(pendingSend.id, email)
                                   }
                               }}/>
        </>
    )
}